/**
 * 앱(Capacitor) ↔ 웹 브릿지 유틸
 * - 네이티브 여부 판별
 * - 결제 화면(AI 이용권 구매)을 외부 브라우저로 위임 (Google Play 결제 정책 대응)
 *   원타임 코드로 로그인 상태를 웹에 동기화
 */
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { apiBaseUrl, SITE_URL } from '../config/runtime';

export const isNativeApp = (): boolean => Capacitor.isNativePlatform();

/** 네이티브 플랫폼 종류('ios'|'android') — 웹이면 null */
export const nativePlatform = (): 'ios' | 'android' | null =>
  Capacitor.isNativePlatform() ? (Capacitor.getPlatform() as 'ios' | 'android') : null;

/**
 * iOS 네이티브 앱 여부 — Apple 앱 심사 가이드라인 3.1.1 대응 분기 전용.
 * 인앱결제(IAP) 외의 결제 수단을 "언급"만 해도(버튼·링크 없이 텍스트만이어도) 심사 리젝 사유가
 * 될 수 있어(2020년 Basecamp/HEY 사례 등), iOS에서는 결제 관련 UI·문구·외부 브라우저 이동을
 * 전부 숨기고 "이용권 부족" 상태만 보여준다. Android는 Google Play 정책상 허용되어 기존과 동일.
 */
export const isIOSApp = (): boolean => nativePlatform() === 'ios';

/** 웹 공개 URL — 앱에서는 항상 서비스 도메인(https://freematch.uk) 사용 */
function externalWebUrl(path: string): string {
  const base = isNativeApp() ? SITE_URL : window.location.origin;
  return `${base}${path}`;
}

/**
 * 외부 브라우저로 지정된 경로를 연다. 로그인 상태면 원타임 코드를 발급받아
 * 자동 로그인 URL로 연다(웹으로 로그인 세션을 동기화). 결제 관련 화면(AI 이용권 구매)을
 * 앱 밖으로 위임할 때 공통으로 사용 — Google Play 결제 정책 대응.
 */
export async function openWebExternally(path: string): Promise<void> {
  console.log('[AppBridge] 외부 브라우저로 이동:', path);
  let finalPath = path;
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const res = await fetch(apiBaseUrl('/membership/app-link'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) {
        const code = new URL(d.data.path, 'https://freematch.uk').searchParams.get('code');
        finalPath = `${path}${path.includes('?') ? '&' : '?'}code=${code}`;
      }
    }
  } catch (e) {
    console.error('[AppBridge] app-link 발급 실패 (기본 경로 사용):', e);
  }
  const url = externalWebUrl(finalPath);
  if (isNativeApp()) {
    await Browser.open({ url }); // Android: Chrome Custom Tabs
  } else {
    window.open(url, '_blank');
  }
}

/**
 * 외부 브라우저로 AI 이용권 구매 페이지 열기 (V4.0)
 */
export async function openAICreditsExternally(toolType: string): Promise<void> {
  await openWebExternally(`/ai-credits/${toolType}`);
}
